import { useCallback, useContext, useRef } from 'react';

import { Feature } from 'config/features';
import { ContactContext } from 'context/ContactContext';

import { useFeature } from 'hooks/useFeature';
import { useLocalStorage } from 'hooks/useLocalStorage';

import { CommunicationContext } from 'utility/Provide/Context/PageContext';

import { ChattyClientDetailOfferParams } from 'types/chatty/offerParams';
import {
    BridgeEvent,
    BridgeInfoDetails,
    BridgeResponse,
    BridgeResponseContext,
    BridgeResponseEvents,
    ConnectionInfoDetails,
} from 'types/legacy/wireframe';

import { chatConfig } from '../__config/settings';
import { getPageScope, getServiceCode } from '../__helper/getChatBotConfig';
import { hasChanges } from '../__helper/hasChanges';
import { ChattyContextEvent } from '../types';

import { useFallbackUrl } from './useFallbackUrl';
import { useUpdateCsCode } from './useUpdateCsCode';

export const useChatty = (offerData?: ChattyClientDetailOfferParams) => {
    const { mainConfig, transactionId, check24Wireframe } =
        useContext(ContactContext);
    const { _env } = useContext(CommunicationContext);
    const { updateCsCode } = useUpdateCsCode();
    const { setItemPhpSessionScope } = useLocalStorage();
    const connectionInfoDetailRef = useRef<ConnectionInfoDetails>({
        serviceCode: getServiceCode(),
        pageScope: getPageScope(_env),
        transactionId: transactionId,
        plugin: chatConfig.bridgeConfig.plugin,
    });
    if (offerData) {
        connectionInfoDetailRef.current = {
            ...connectionInfoDetailRef.current,
            offer: offerData,
        };
    }
    const bridgeInfoDetailRef = useRef<BridgeInfoDetails>({
        product_key: chatConfig.bridgeConfig.product_id,
        type: BridgeEvent.Event,
        context: {
            ...connectionInfoDetailRef.current,
        },
    });
    const bridgeResponseRef = useRef<BridgeResponse>({
        product_key: chatConfig.bridgeConfig.product_id,
        type: BridgeEvent.Response,
        correlation_id: '',
        data: {},
    });
    const isChattyActive = useFeature(Feature.Chatty);
    const isForeignDomain = _env.foreignDomain;
    const isChatActive = isChattyActive && !isForeignDomain;
    const openHelpUrl = useFallbackUrl();
    const chattyFallBack = useCallback(() => {
        openHelpUrl();
    }, [openHelpUrl]);

    const updateBridgeContext = useCallback(() => {
        bridgeInfoDetailRef.current = {
            ...bridgeInfoDetailRef.current,
            context: {
                ...connectionInfoDetailRef.current,
            },
        };
    }, [connectionInfoDetailRef, bridgeInfoDetailRef]);
    const updateConnectionInfoDetailRef = useCallback(
        (data?: Partial<ConnectionInfoDetails>) => {
            connectionInfoDetailRef.current = {
                ...data,
                plugin: chatConfig.bridgeConfig.plugin,
                serviceCode: connectionInfoDetailRef.current.serviceCode,
                pageScope: getPageScope(_env),
                transactionId: transactionId,
            };
            if (data?.serviceCode) {
                connectionInfoDetailRef.current.serviceCode = data.serviceCode;
            }
            if (offerData) {
                connectionInfoDetailRef.current.offer = offerData;
            }

            updateBridgeContext();
        },
        [transactionId, _env, updateBridgeContext, offerData]
    );

    const updateDetails = useCallback(
        (data?: Partial<ConnectionInfoDetails>, openChatty?: boolean) => {
            if (!isChatActive) {
                return;
            }

            const isChattyOpen =
                !!check24Wireframe?.loader?.chatty?.checkActiveChatty();

            if (hasChanges(connectionInfoDetailRef.current, _env, data)) {
                updateConnectionInfoDetailRef(data);
                const chattySendContext = new CustomEvent(
                    ChattyContextEvent.SEND_CONTEXT,
                    {
                        detail: { context: bridgeInfoDetailRef.current },
                    }
                );
                const chattySendBackgroundContext = new CustomEvent(
                    ChattyContextEvent.BACKGROUND_UPDATE_CONTEXT,
                    {
                        detail: {
                            chatty: {
                                configname: mainConfig?.name,
                                connectionInfoDetails:
                                    connectionInfoDetailRef.current,
                            },
                        },
                    }
                );

                if (
                    !!check24Wireframe?.loader?.chatty?.listenerInitilized &&
                    !!transactionId
                ) {
                    document.dispatchEvent(
                        isChattyOpen
                            ? chattySendContext
                            : chattySendBackgroundContext
                    );
                }
            }
            if (!isChattyOpen && openChatty) {
                setTimeout(() => {
                    check24Wireframe?.loader.openChatbot(mainConfig);
                }, 200);
            }
        },
        [
            check24Wireframe,
            updateConnectionInfoDetailRef,
            _env,
            mainConfig,
            transactionId,
            isChatActive,
        ]
    );

    const responseBridgeUpdate = useCallback(
        async (data: BridgeResponseContext) => {
            const responseData = data?.context;
            const sendContext = () => {
                if (!!check24Wireframe?.loader?.chatty?.checkActiveChatty()) {
                    setTimeout(() => {
                        const chattySendContext = new CustomEvent(
                            ChattyContextEvent.SEND_CONTEXT,
                            {
                                detail: { context: bridgeResponseRef.current },
                            }
                        );

                        document.dispatchEvent(chattySendContext);
                    }, 500);
                }
            };

            if (responseData.action) {
                if (
                    responseData?.action ===
                        BridgeResponseEvents.ApplyVoucher &&
                    !!responseData?.data?.cs_code
                ) {
                    const updateData = await updateCsCode(
                        responseData?.data?.cs_code as string
                    );

                    bridgeResponseRef.current = {
                        ...bridgeResponseRef.current,
                        correlation_id: responseData.correlation_id,
                        data: updateData,
                    };

                    sendContext();
                }
                if (responseData?.action === BridgeResponseEvents.GetContext) {
                    bridgeResponseRef.current = {
                        ...bridgeResponseRef.current,
                        correlation_id: responseData.correlation_id,
                        data: {
                            serviceCode: getServiceCode(),
                            transactionId: transactionId,
                            pageScope: getPageScope(_env),
                            plugin: chatConfig.bridgeConfig.plugin,
                        },
                    };

                    if (offerData) {
                        bridgeResponseRef.current.data.offer = offerData;
                    }

                    sendContext();
                }
            }
        },
        [
            check24Wireframe?.loader.chatty,
            updateCsCode,
            _env,
            offerData,
            transactionId,
        ]
    );

    const openChatty = useCallback(
        (data?: Partial<ConnectionInfoDetails>) => {
            if (isChatActive) {
                setItemPhpSessionScope('closeInactiveTabChatty', '1');
                updateDetails(data, true);
            } else {
                chattyFallBack();
            }
        },
        [updateDetails, isChatActive, chattyFallBack, setItemPhpSessionScope]
    );

    window.addEventListener('storage', function (event) {
        if (
            event.key &&
            event.key.indexOf('closeInactiveTabChatty') > -1 &&
            check24Wireframe?.loader.chatty?.checkActiveChatty()
        ) {
            check24Wireframe?.loader.chatty?.close();
        }
    });

    return {
        openChatty,
        updateDetails,
        responseBridgeUpdate,
        connectionInfoDetailRef,
        check24Wireframe,
    };
};
